/**
 * OrchestratorLogo — brand mark voor het Orchestrator platform.
 *
 * Concept: een centrale hub (de orchestrator/human) met 4 satellite-nodes
 * (de agents) die door lijnen verbonden zijn — visualiseert een dirigent
 * die AI-agents aanstuurt. Kleine "beat"-lijntjes onderaan suggereren
 * een dirigeerbaton.
 *
 * Twee varianten:
 *   • `<OrchestratorLogo />`           → alleen het mark, kleur = brand
 *   • `<OrchestratorLogo withText />`  → mark + "Orchestrator" wordmark
 */
import React from "react";

export default function OrchestratorLogo({
    size = 28,
    withText = false,
    className = "",
    textClassName = "",
}) {
    const stroke = "currentColor";
    return (
        <span className={`inline-flex items-center gap-2 ${className}`} data-testid="orchestrator-logo">
            <svg
                width={size} height={size} viewBox="0 0 32 32" fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-[var(--jr-brand)]"
                aria-label="Orchestrator"
            >
                {/* Outer ring — subtle */}
                <circle cx="16" cy="16" r="14" stroke={stroke} strokeWidth="1.2" opacity="0.28"/>

                {/* Connection lines from hub to satellites */}
                <line x1="16" y1="16" x2="6"  y2="8"  stroke={stroke} strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
                <line x1="16" y1="16" x2="26" y2="8"  stroke={stroke} strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
                <line x1="16" y1="16" x2="6"  y2="24" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
                <line x1="16" y1="16" x2="26" y2="24" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>

                {/* Satellite agent nodes */}
                <circle cx="6"  cy="8"  r="2.4" fill={stroke} opacity="0.85"/>
                <circle cx="26" cy="8"  r="2.4" fill={stroke} opacity="0.85"/>
                <circle cx="6"  cy="24" r="2.4" fill={stroke} opacity="0.85"/>
                <circle cx="26" cy="24" r="2.4" fill={stroke} opacity="0.85"/>

                {/* Central hub (the human orchestrator) — filled with a subtle inner ring */}
                <circle cx="16" cy="16" r="4.2" fill={stroke}/>
                <circle cx="16" cy="16" r="1.6" fill="#000000" opacity="0.85"/>
            </svg>
            {withText && (
                <span className={`font-display tracking-tight ${textClassName || "text-lg"}`}>
                    Orchestrator
                </span>
            )}
        </span>
    );
}


/**
 * Solid box variant (voor sidebar/dashboard header waar we een kleine
 * gekleurde tegel willen — vervangt de oude "J" tegel). Volledig
 * op de brand-accent kleur, met witte agents/lijnen erop.
 */
export function OrchestratorTile({ size = 28, className = "" }) {
    return (
        <div
            className={`grid place-items-center rounded-md bg-[var(--jr-brand)] ${className}`}
            style={{ width: size, height: size }}
            data-testid="orchestrator-tile"
        >
            <svg width={Math.round(size * 0.72)} height={Math.round(size * 0.72)} viewBox="0 0 32 32" fill="none">
                <line x1="16" y1="16" x2="6"  y2="8"  stroke="#000" strokeWidth="1.6" strokeLinecap="round" opacity="0.55"/>
                <line x1="16" y1="16" x2="26" y2="8"  stroke="#000" strokeWidth="1.6" strokeLinecap="round" opacity="0.55"/>
                <line x1="16" y1="16" x2="6"  y2="24" stroke="#000" strokeWidth="1.6" strokeLinecap="round" opacity="0.55"/>
                <line x1="16" y1="16" x2="26" y2="24" stroke="#000" strokeWidth="1.6" strokeLinecap="round" opacity="0.55"/>
                <circle cx="6"  cy="8"  r="2.6" fill="#000" opacity="0.85"/>
                <circle cx="26" cy="8"  r="2.6" fill="#000" opacity="0.85"/>
                <circle cx="6"  cy="24" r="2.6" fill="#000" opacity="0.85"/>
                <circle cx="26" cy="24" r="2.6" fill="#000" opacity="0.85"/>
                <circle cx="16" cy="16" r="4.4" fill="#000"/>
            </svg>
        </div>
    );
}
